import React, { FC } from 'react';
import { useTranslation } from 'Shared/Hooks/useTranslation';

import crehanaTracking from 'Jsx/Utils/crehanaTracking';
import { ProductsContext } from 'Jsx/ContextApis/Products';

import ShoppingCartLight from 'crehana-icons/ShoppingCartLight';
import ShoppingCartAdded from 'crehana-icons/ShoppingCartAdded';

import Text from 'crehana-ui/Text';
import { primaryMain, secondaryMain } from 'crehana-ui/styles/colors';
import Share2 from 'crehana-icons/Share2';
import Gift2 from 'crehana-icons/Gift2';

import { COURSE_DETAIL_TRANSLATION_KEY } from 'Jsx/Utils/transformKeys';

import Loadable from 'Jsx/Shared/Loadable';
import Wishlist from './components/Wishlist';

interface ShareLinksProps {
  course: {
    originalId: number;
    title: string;
    promoImage: string;
    professor: {
      profilePictureUrl: string;
      fullName: string;
    };
    prices: {
      currencySymbol: string;
      price: number;
      realPrice: number;
    };
    url: string;
  };
  showAddToCartButton?: boolean;
  hideShareButton?: boolean;
  hideWishlist?: boolean;
  hideGiftButton?: boolean;
  showShareLabel?: boolean;
}

const ShareLinks: FC<ShareLinksProps> = ({
  course: { originalId, title, promoImage, professor },
  course,
  showAddToCartButton,
  hideShareButton,
  hideWishlist,
  hideGiftButton,
  showShareLabel,
}) => {
  const { t } = useTranslation(COURSE_DETAIL_TRANSLATION_KEY);
  const [giftcardModal, setGiftcarModal] = React.useState(false);
  const [shareModal, setShareModal] = React.useState(false);
  const {
    shoppingCartState: { courseIdsInCart },
    addCourseToCart,
    removeCourseFromCart,
  } = React.useContext(ProductsContext);

  const handleClickShareWithFriends = () => {
    setShareModal(true);
    crehanaTracking(
      crehanaTracking.TYPES.MIXPANEL,
      'COURSE_DETAIL_SHARE_WITH_BUTTON',
    );
    return null;
  };

  const isInShoppingCart = courseIdsInCart.includes(originalId);
  const toggleShoppingCart = () => {
    if (!isInShoppingCart) {
      addCourseToCart({ ...course, courseId: originalId });
    } else {
      removeCourseFromCart({ ...course, courseId: originalId });
    }
  };

  const CartButton = (
    <button
      type="button"
      onClick={toggleShoppingCart}
      className="cursor-pointer mr-20"
      aria-label="toggle-cart-button"
    >
      {isInShoppingCart ? (
        <ShoppingCartAdded color={primaryMain} size={27} />
      ) : (
        <ShoppingCartLight color={primaryMain} size={27} />
      )}
    </button>
  );

  const WishlistButton = (
    <Wishlist isInShoppingCart={isInShoppingCart} course={course} />
  );

  const ShareButton = (
    <button
      className="flex items-center mr-20"
      type="button"
      onClick={handleClickShareWithFriends}
      aria-label="share"
    >
      <Share2 size={20} color={primaryMain} />
      {showShareLabel && (
        <Text type="link" className="ml-16" color="primary-main">
          {t('SHARE_WITH_FRIENDS')}
        </Text>
      )}
    </button>
  );

  const GiftButton = (
    <div className="flex ml-auto">
      <button
        className="flex items-center cursor-pointer outline-none"
        onClick={() => setGiftcarModal(true)}
        onKeyPress={() => setGiftcarModal(true)}
        type="button"
        color="secondary-main"
        aria-label="gift-card"
      >
        <Gift2 color={secondaryMain} size={20} />
        <Text className="ml-8" type="link" color="secondary-main">
          {t('DETAIL_GIFTCARD')}
        </Text>
      </button>
    </div>
  );

  return (
    <div className="w-full mt-24">
      <div className="flex items-center">
        {showAddToCartButton && CartButton}
        {!hideWishlist && WishlistButton}
        {!hideShareButton && ShareButton}
        {!hideGiftButton && GiftButton}
      </div>
      {/* Dialogs */}
      {!hideShareButton && (
        <Loadable
          module={() =>
            import(
              /* webpackChunkName: "course-detail-share-modal" */ './components/ShareModal'
            )
          }
          promoImage={promoImage}
          title={title}
          professor={professor}
          open={shareModal}
          onDismiss={() => setShareModal(false)}
        />
      )}
      {!hideGiftButton && (
        <Loadable
          module={() =>
            import(
              /* webpackChunkName: "course-detail-gift-card-modal" */ 'Jsx/Shared/GiftCardModal'
            )
          }
          open={giftcardModal}
          onRequestClose={() => setGiftcarModal(false)}
          giftcardData={{
            itemId: course.originalId,
            itemType: 'course',
            price: course.prices.price,
            currencySymbol: course.prices.currencySymbol,
            title: course.title,
          }}
        />
      )}
    </div>
  );
};

export default ShareLinks;
